import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router'
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { observer } from 'mobx-react-lite'
import {
  Autocomplete,
  Avatar, Box, Button, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, IconButton, MenuItem,
  Paper, Tooltip, Typography,
  Menu,
} from '@mui/material'
import { DataTable, type Column } from '../components/ui/DataTable'
import {
  TrendingUp as LeadsIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  OpenInNew as ViewIcon,
  Phone as CallIcon,
  CalendarMonth as FollowUpIcon,
  MoreVert as MoreIcon,
  KeyboardArrowUp as ScrollTopIcon,
} from '@mui/icons-material'
import { toast } from 'react-toastify'
import { PageHeader } from '../components/shared/PageHeader'
import {
  leadsApi, tagsApi, Lead, LeadFilters, LeadStatus,
  LEAD_STATUSES,
  SOURCE_LABELS, STATUS_LABELS, UpdateLeadPayload, Tag,
} from '../lib/api/leadsApi'
import { StatusBadge } from '../components/shared/UniversalActivityTimeline'
import { PageActionButtons } from '../components/shared/PageActionButtons'
import { type LeadFormData } from '../lib/schemas/leadSchema'
import { ListToolbar, type BulkAction } from '../components/shared/ListToolbar'
import { UnifiedFilters, EMPTY_UNIFIED_FILTERS, FilterDef, countActiveFilters } from '../components/shared/UnifiedFilterDialog'
import { themeColors } from '../app/layout/theme'
import { authStore } from '../lib/stores/authStore'
import { orgsApi, lobsApi } from "../lib/api/tenantsApi"
import { usersAdminApi, type AdminUser } from '../lib/api/usersAdminApi'
import { getCustomerTypes, type CustomerType } from '../lib/api/customerTypesApi'
import { useOrgFilter, useOrgSelector } from '../context/OrgSelectorContext'
import { FollowUpModal } from '../components/leads/FollowUpModal'
import {
  callLogsApi, CallLog, CallStatus,
  SaveOutcomePayload,
} from '../lib/api/callLogsApi'
import { callStore } from '../lib/stores/callStore'
import { DeleteDialog } from '../components/shared/DeleteDialog'
import { ConfirmDialog } from '../components/shared/ConfirmDialog'
import { ConvertLeadFlow } from '../components/leads/ConvertLeadFlow'
import { LeadDrawer } from '../components/drawers/LeadDrawer'
import { EditLeadDialog } from '../components/leads/EditLeadDialog'
import { bulkUploadStore } from '../lib/stores/bulkUploadStore'
import { EditActionIcon, DeleteActionIcon } from '../components/ui/ActionIcons'
import { FormInput } from '../components/ui/FormInput'
import { usePermission } from '../lib/hooks/usePermission'
import { useLeadSourceFilterOptions } from '../lib/hooks/useLeadSourceFilterOptions'
import { maskPhoneNumber } from '../utils/phoneUtils'


function formatDate(iso: string | null | undefined) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime()) || d.getFullYear() <= 1970) return '—'
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

function getInitials(name: string) {
  return name.split(' ').slice(0, 2).map(n => n[0]?.toUpperCase() ?? '').join('')
}

function getAvatarStyle(name: string) {
  return { bgcolor: `${themeColors.primary}12`, color: themeColors.primary };
}

const UNASSIGN_LEAD_USER: AdminUser = {
  id: '__UNASSIGN__',
  name: 'Unassign',
  email: 'Remove assignee from selected leads',
  roleName: '',
  orgId: '',
} as unknown as AdminUser

function formatRelative(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60_000)
  const hours = Math.floor(diff / 3_600_000)
  const days = Math.floor(diff / 86_400_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days < 7) return `${days}d ago`
  if (days < 30) return `${Math.floor(days / 7)}w ago`
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
}

// ── Call Flow Hook removed (using global callStore)

// ── Row Actions Menu ──────────────────────────────────────────────────────────

function RowActionsMenu({ canDelete, showCall = true, onCall, onFollowUp, onDelete }: {
  canDelete: boolean
  showCall?: boolean
  onCall: () => void
  onFollowUp: () => void
  onDelete: () => void
}) {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  const open = Boolean(anchor)
  const close = () => setAnchor(null)

  return (
    <>
      <Tooltip title="More actions">
        <IconButton size="small" onClick={e => { e.stopPropagation(); setAnchor(e.currentTarget) }}
          sx={{ color: themeColors.text.muted, '&:hover': { color: themeColors.primary, bgcolor: themeColors.primaryLight + '15' } }}>
          <MoreIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={open} onClose={close}
        onClick={e => e.stopPropagation()}
        PaperProps={{
          elevation: 3, sx: {
            borderRadius: '10px', minWidth: 160, mt: 0.5,
            '& .MuiMenuItem-root': { fontSize: '13px', gap: 1.2, px: 1.5, py: 0.9 }
          }
        }}>
        {showCall && (
          <MenuItem onClick={() => { close(); onCall() }}>
            <CallIcon sx={{ fontSize: 15, color: '#7b1fa2' }} />
            Log call
          </MenuItem>
        )}
        <MenuItem onClick={() => { close(); onFollowUp() }}>
          <FollowUpIcon sx={{ fontSize: 15, color: '#0070d2' }} />
          Schedule follow-up
        </MenuItem>
        {canDelete && <Divider />}
        {canDelete && (
          <MenuItem onClick={() => { close(); onDelete() }} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ fontSize: 15 }} />
            Delete
          </MenuItem>
        )}
      </Menu>
    </>
  )
}

// ── Kanban Board ──────────────────────────────────────────────────────────────

const KANBAN_COLUMNS: { status: LeadStatus; color: string; bg: string }[] = [
  { status: 'NEW', color: '#1d4ed8', bg: '#eff6ff' },
  { status: 'CONTACTED', color: '#b45309', bg: '#fffbeb' },
  { status: 'QUALIFIED', color: '#6d28d9', bg: '#f5f3ff' },
  { status: 'CONVERTED', color: '#15803d', bg: '#f0fdf4' },
  { status: 'JUNK', color: '#dc2626', bg: '#fef2f2' },
]

function KanbanCard({ lead, onOpen, onCall, onFollowUp, onDragStart: onDragStartProp, onDragEnd: onDragEndProp }: {
  lead: Lead
  onOpen: () => void
  onCall: () => void
  onFollowUp: () => void
  onDragStart?: () => void
  onDragEnd?: () => void
}) {
  const isJunk = lead.status === 'JUNK'
  return (
    <Paper
      elevation={0}
      onClick={onOpen}
      draggable={true}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', lead.id)
        onDragStartProp?.()
      }}
      onDragEnd={() => onDragEndProp?.()}
      sx={{
        p: 1.5, mb: 1, borderRadius: '10px',
        border: `1px solid ${isJunk ? '#fca5a5' : themeColors.border}`,
        cursor: 'grab', bgcolor: isJunk ? '#fff8f8' : '#fff',
        opacity: isJunk ? 0.85 : 1,
        '&:hover': { borderColor: isJunk ? '#dc2626' : themeColors.primary, boxShadow: `0 2px 10px ${isJunk ? '#dc262618' : themeColors.primary + '18'}` },
        '&:active': { cursor: 'grabbing' },
        transition: 'all 0.15s',
      }}
    >
      {/* Name */}
      <Typography fontWeight={700} fontSize="13px" color="text.primary" sx={{ lineHeight: 1.3, mb: 0.3 }}>
        {lead.name}
      </Typography>

      {/* Org name (SuperAdmin multi-tenant context) */}
      {lead.orgName && (
        <Typography fontSize="12px" fontWeight={500} sx={{ color: themeColors.primary, mb: 0.3 }}>
          {lead.orgName}
        </Typography>
      )}

      {/* Email */}
      {lead.email && (
        <Typography fontSize="11.5px" color="text.secondary" noWrap sx={{ mb: 0.3 }}>
          {lead.email}
        </Typography>
      )}

      {/* Phone */}
      <Typography fontSize="11.5px" color="text.secondary" sx={{ mb: lead.tags.length ? 0.8 : 1 }}>
        {maskPhoneNumber(lead.phone)}
      </Typography>

      {/* Tags */}
      {lead.tags.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.4, mb: 1, flexWrap: 'wrap' }}>
          {lead.tags.slice(0, 2).map(tag => (
            <Chip key={tag.id} label={tag.name} size="small"
              sx={{
                height: 14, fontSize: '9px', fontWeight: 600,
                bgcolor: `${tag.color}18`, color: tag.color,
                border: `1px solid ${tag.color}40`,
                '& .MuiChip-label': { px: '4px' }
              }} />
          ))}
          {lead.tags.length > 2 && (
            <Typography fontSize="9px" color="text.secondary" alignSelf="center">
              +{lead.tags.length - 2}
            </Typography>
          )}
        </Box>
      )}

      {/* Footer: assignee + time */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {lead.assignedTo ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Avatar sx={{
              width: 22, height: 22, fontSize: '9px', fontWeight: 700,
              ...getAvatarStyle(lead.assignedTo.name),
            }}>
              {getInitials(lead.assignedTo.name)}
            </Avatar>
            <Typography fontSize="11.5px" color="text.secondary" noWrap sx={{ maxWidth: 110 }}>
              {lead.assignedTo.name}
            </Typography>
          </Box>
        ) : !isJunk ? (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.3, cursor: 'pointer',
            color: themeColors.primary,
            '&:hover': { opacity: 0.75 },
          }}
            onClick={e => { e.stopPropagation(); onOpen() }}>
            <Typography fontSize="11.5px" fontWeight={600} color={themeColors.primary}>
              Assign owner
            </Typography>
            <Typography fontSize="12px" color={themeColors.primary} sx={{ mt: '1px' }}>›</Typography>
          </Box>
        ) : <Box />}
        <Typography fontSize="11px" color="text.secondary">
          {formatRelative(lead.updatedAt)}
        </Typography>
      </Box>

      {/* Quick-action strip */}
      <Box sx={{
        display: 'flex', gap: 0.5, mt: 1, pt: 0.75,
        borderTop: `1px solid ${themeColors.border}`,
      }}
        onClick={e => e.stopPropagation()}>
        {lead.status !== 'JUNK' && (
          <Tooltip title={lead.status === 'CONVERTED' ? "Lead is converted" : "Log call"}>
            <Box component="span" sx={{ display: 'inline-flex', cursor: lead.status === 'CONVERTED' ? 'not-allowed' : 'inherit' }}>
              <IconButton size="small" onClick={onCall}
                disabled={lead.status === 'CONVERTED'}
                sx={{ color: themeColors.text.muted, '&:hover': { color: '#7b1fa2', bgcolor: '#7b1fa215' } }}>
                <CallIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Box>
          </Tooltip>
        )}
        {!isJunk && (
          <Tooltip title="Schedule follow-up">
            <IconButton size="small" onClick={onFollowUp}
              sx={{ color: themeColors.text.muted, '&:hover': { color: '#0070d2', bgcolor: themeColors.primaryLight + '15' } }}>
              <FollowUpIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Open details">
          <IconButton size="small" onClick={onOpen}
            sx={{ color: themeColors.text.muted, '&:hover': { color: themeColors.primary, bgcolor: themeColors.primaryLight + '15' } }}>
            <ViewIcon sx={{ fontSize: 13 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  )
}

function LeadKanbanColumn({
  col,
  statusCount,
  baseFilters,
  onOpen,
  onCall,
  onFollowUp,
  onAddLead,
  canCreate,
  onDropLead,
  setIsDraggingBoard,
}: {
  col: { status: LeadStatus; color: string; bg: string }
  statusCount?: number
  baseFilters: LeadFilters
  onOpen: (lead: Lead) => void
  onCall: (lead: Lead) => void
  onFollowUp: (lead: Lead) => void
  onAddLead: () => void
  canCreate: boolean
  onDropLead: (leadId: string, targetStatus: LeadStatus) => void
  setIsDraggingBoard: (val: boolean) => void
}) {
  const [isOver, setIsOver] = useState(false)

  const isConvertedCol = col.status === 'CONVERTED'
  const isConvertedFiltered = baseFilters.statuses?.includes('CONVERTED') ?? false
  const isEnabled = !isConvertedCol || isConvertedFiltered

  const colFilters = useMemo(() => ({
    ...baseFilters,
    statuses: [col.status],
    pageSize: 20,
  }), [baseFilters, col.status])

  const {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ['leads-kanban-col', col.status, colFilters],
    queryFn: ({ pageParam = 1 }) =>
      leadsApi.getAll({ ...colFilters, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const fetched = allPages.flatMap(p => p.items).length
      return fetched < lastPage.total ? allPages.length + 1 : undefined
    },
    staleTime: 0,
    enabled: isEnabled,
  })

  const colLeads = useMemo(() => {
    if (!isEnabled || !data?.pages) return []
    const map = new Map<string, Lead>()
    for (const page of data.pages) {
      for (const item of page.items) {
        map.set(item.id, item)
      }
    }
    return Array.from(map.values())
  }, [data, isEnabled])

  const count = isConvertedCol && !isConvertedFiltered
    ? 0
    : (statusCount ?? data?.pages?.[0]?.total ?? colLeads.length)

  return (
    <Box
      onDragOver={(e) => { e.preventDefault(); setIsOver(true) }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setIsOver(false)
        }
      }}
      onDrop={(e) => {
        e.preventDefault()
        const leadId = e.dataTransfer.getData('text/plain')
        if (leadId) onDropLead(leadId, col.status)
        setIsOver(false)
        setIsDraggingBoard(false)
      }}
      sx={{
        flex: 1, minWidth: 0,
        display: 'flex', flexDirection: 'column',
        borderRadius: '12px',
        bgcolor: isOver ? `${col.color}18` : 'transparent',
        borderLeft: isOver ? `4px solid ${col.color}` : '4px solid transparent',
        boxShadow: isOver ? `inset 0 0 0 1.5px ${col.color}40` : 'none',
        transition: 'background-color 0.15s ease, border-left 0.15s ease, box-shadow 0.15s ease',
        px: isOver ? 0.5 : 0,
      }}
    >
      {/* Sticky column header */}
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 10, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        mb: 1.25, px: 0.5, py: 0.75, borderRadius: '8px',
        bgcolor: isOver ? `${col.color}22` : 'rgba(247,248,250,0.97)',
        backdropFilter: 'blur(8px)',
        borderBottom: isOver ? `2px solid ${col.color}60` : '2px solid transparent',
        transition: 'all 0.15s ease',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: col.color, flexShrink: 0 }} />
          <Typography fontWeight={700} fontSize="13px" sx={{ color: isOver ? col.color : 'text.primary' }}>
            {STATUS_LABELS[col.status]}
          </Typography>
          <Box sx={{ px: '7px', py: '1px', borderRadius: '20px', bgcolor: isOver ? col.color : col.bg, border: `1px solid ${col.color}35` }}>
            <Typography fontSize="11px" fontWeight={700} sx={{ color: isOver ? '#fff' : col.color }}>
              {isLoading ? '…' : count}
            </Typography>
          </Box>
        </Box>
        {canCreate && col.status === 'NEW' && (
          <Tooltip title={`Add ${STATUS_LABELS[col.status]} lead`}>
            <IconButton size="small" onClick={onAddLead} sx={{
              width: 24, height: 24, bgcolor: col.bg, color: col.color,
              border: `1px solid ${col.color}35`,
              '&:hover': { bgcolor: col.color, color: '#fff' },
            }}>
              <AddIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Coloured top-border + cards — scrollable inside column */}
      <Box
        onScroll={(e) => {
          const el = e.currentTarget
          if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
            if (hasNextPage && !isFetchingNextPage) {
              fetchNextPage()
            }
          }
        }}
        sx={{
          flex: 1, borderTop: `3px solid ${col.color}`, pt: 1.5, minHeight: 150,
          maxHeight: 'calc(100vh - 240px)', overflowY: 'auto', pr: 0.5,
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': { bgcolor: `${col.color}40`, borderRadius: '4px' },
          '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
          ...(isOver && colLeads.length === 0 ? { display: 'flex', flexDirection: 'column', justifyContent: 'center' } : {}),
        }}
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
            <CircularProgress size={22} sx={{ color: col.color }} />
          </Box>
        ) : colLeads.length === 0 ? (
          <Box sx={{
            p: 3, borderRadius: '10px', textAlign: 'center',
            border: isOver ? `2px solid ${col.color}` : `1.5px dashed ${col.color}40`,
            bgcolor: isOver ? `${col.color}12` : col.bg,
            transition: 'all 0.15s ease', mx: 0.5,
          }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: '50%',
              bgcolor: isOver ? col.color : `${col.color}18`,
              border: `1px solid ${col.color}35`, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1,
            }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: isOver ? '#fff' : col.color, opacity: isOver ? 1 : 0.5 }} />
            </Box>
            <Typography fontSize="12px" fontWeight={700} sx={{ color: col.color }}>
              {isOver
                ? `Move to ${STATUS_LABELS[col.status]}`
                : isConvertedCol && !isConvertedFiltered
                  ? 'Converted leads hidden'
                  : 'No leads'}
            </Typography>
            <Typography fontSize="11px" color="text.secondary" sx={{ mt: 0.25 }}>
              {isOver
                ? 'Release to drop here'
                : isConvertedCol && !isConvertedFiltered
                  ? 'Select "Converted" in status filter to load'
                  : 'Nothing here yet'}
            </Typography>
          </Box>
        ) : (
          <>
            {colLeads.map(lead => (
              <KanbanCard key={lead.id} lead={lead}
                onOpen={() => onOpen(lead)}
                onCall={() => onCall(lead)}
                onFollowUp={() => onFollowUp(lead)}
                onDragStart={() => setIsDraggingBoard(true)}
                onDragEnd={() => setIsDraggingBoard(false)} />
            ))}
            {isFetchingNextPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={16} sx={{ color: col.color }} />
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  )
}

function KanbanBoard({
  statusCounts = {},
  baseFilters,
  onOpen,
  onCall,
  onFollowUp,
  onAddLead,
  canCreate,
  onDropLead,
}: {
  statusCounts?: Record<string, number>
  baseFilters: LeadFilters
  onOpen: (lead: Lead) => void
  onCall: (lead: Lead) => void
  onFollowUp: (lead: Lead) => void
  onAddLead: () => void
  canCreate: boolean
  onDropLead: (leadId: string, targetStatus: LeadStatus) => void
}) {
  const [, setIsDraggingBoard] = useState(false)

  return (
    <Box sx={{ display: 'flex', gap: 2, pb: 2, alignItems: 'stretch', width: '100%' }}>
      {KANBAN_COLUMNS.map(col => (
        <LeadKanbanColumn
          key={col.status}
          col={col}
          statusCount={statusCounts[col.status]}
          baseFilters={baseFilters}
          onOpen={onOpen}
          onCall={onCall}
          onFollowUp={onFollowUp}
          onAddLead={onAddLead}
          canCreate={canCreate}
          onDropLead={onDropLead}
          setIsDraggingBoard={setIsDraggingBoard}
        />
      ))}
    </Box>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// Main Page
// ══════════════════════════════════════════════════════════════════════════════

const LeadsPage = observer(function LeadsPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { hasAction } = usePermission()
  // SuperAdmin detection: OrgId/LobId-based (matches the navbar org selector and the
  // backend's ITenantContext convention), not usePermission's roleName-string match —
  // that string check doesn't reliably match every SuperAdmin's role label, which
  // previously hid the Organisation/LOB filter tabs entirely for SuperAdmin here.
  const { isSuperAdmin } = useOrgSelector()
  const canCreate = hasAction('/leads', 'create')
  const canEdit = hasAction('/leads', 'update')
  const canDelete = hasAction('/leads', 'delete')
  const canAssign = hasAction('/leads', 'assign')
  const canBulkUpload = hasAction('/leads', 'bulk_upload')
  const canExport = hasAction('/leads', 'export')
  const canCallLog = hasAction('/leads', 'call_log')

  // Org Admin tier (mirrors backend ITenantContext convention): has an orgId but no
  // specific lobId — sees/filters across every LOB in their own org, unlike a Normal User.
  const userOrgId = authStore.user?.orgId ?? ''
  const userLobId = authStore.user?.lobId ?? ''
  const isOrgAdmin = !isSuperAdmin && !!userOrgId && !userLobId

  // Global org filter from navbar (SuperAdmin only); local page dropdowns take precedence when set
  const navOrgFilter = useOrgFilter()

  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<UnifiedFilters>(() => {
    const saved = sessionStorage.getItem('crm-leads-filters')
    if (saved) {
      try { return JSON.parse(saved) } catch { }
    }
    return EMPTY_UNIFIED_FILTERS
  })
  const [page, setPage] = useState(1)
  const [pageSizeTable, setPageSizeTable] = useState(() => {
    const saved = localStorage.getItem('crm_leads_page_size')
    return saved ? Number(saved) : 10
  })

  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const [showScrollTop, setShowScrollTop] = useState(false)

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setShowScrollTop(e.currentTarget.scrollTop > 200)
  }

  useEffect(() => {
    sessionStorage.setItem('crm-leads-filters', JSON.stringify(filters))
  }, [filters])

  const { data: orgs = [] } = useQuery({ queryKey: ['admin-orgs'], queryFn: orgsApi.getAll, enabled: isSuperAdmin, staleTime: 60_000 })
  // SuperAdmin: prefer the org picked in this page's own filter dialog; fall back to
  // whatever org is selected in the navbar so the LOB list works right after picking
  // an org up top, without needing to also set the local Organisation filter.
  const orgIdForFetching = filters.checkboxes.orgId?.[0] || navOrgFilter.orgId
  // SuperAdmin: LOBs depend on whichever org is in scope (filter or navbar selection).
  // Org Admin: LOBs are always scoped to their own (fixed) org — no org selector needed.
  const lobOrgIdForFetching = isSuperAdmin ? orgIdForFetching : userOrgId
  const { data: lobsForFilter = [] } = useQuery({
    queryKey: ['admin-lobs', lobOrgIdForFetching],
    queryFn: () => lobsApi.getAll(lobOrgIdForFetching!),
    enabled: (isSuperAdmin || isOrgAdmin) && !!lobOrgIdForFetching,
    staleTime: 30_000,
  })

  // Local page filter takes precedence; navbar org filter is the fallback for SuperAdmin
  const effectiveOrgIds = isSuperAdmin && filters.checkboxes.orgId?.length
    ? filters.checkboxes.orgId
    : navOrgFilter.orgId ? [navOrgFilter.orgId] : undefined

  const { data: allTags = [] } = useQuery<Tag[]>({
    queryKey: ['tags', effectiveOrgIds],
    queryFn: () => tagsApi.getAll(effectiveOrgIds && effectiveOrgIds[0] ? { orgId: effectiveOrgIds[0] } : undefined),
    staleTime: 60_000
  })

  const { data: customerTypes = [] } = useQuery<CustomerType[]>({
    queryKey: ['customer-types'],
    queryFn: getCustomerTypes,
    staleTime: 5 * 60 * 1000,
  })

  const { data: usersData } = useQuery({
    queryKey: ['users-for-assign'],
    queryFn: () => usersAdminApi.getAll({ pageSize: 1000 }),
    staleTime: 60_000,
  })
  const usersList = usersData?.items ?? []

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false)
  const [bulkAssignUser, setBulkAssignUser] = useState<AdminUser | null>(null)
  const [newLeadOpen, setNewLeadOpen] = useState(false)
  const [editingLead, setEditingLead] = useState<Lead | null>(null)
  const [deletingLead, setDeletingLead] = useState<Lead | null>(null)
  const [followUpLead, setFollowUpLead] = useState<Lead | null>(null)
  const [convertLead, setConvertLead] = useState<Lead | null>(null)
  const [junkLead, setJunkLead] = useState<Lead | null>(null)
  const [viewMode, setViewMode] = useState<'table' | 'kanban'>(() => {
    const saved = localStorage.getItem('crm_leads_view_mode')
    return (saved === 'kanban' || saved === 'table') ? saved : 'table'
  })

  useEffect(() => {
    localStorage.setItem('crm_leads_view_mode', viewMode)
  }, [viewMode])

  // Global calling store usage

  const KANBAN_PAGE_SIZE = 50

  // ── Base filters (shared between table and kanban queries) ──────────────────
  const baseFilters = {
    search: search || undefined,
    statuses: (filters.checkboxes.status as any) || undefined,
    sources: filters.checkboxes.source || undefined,
    tagIds: filters.checkboxes.tagId || undefined,
    assignedToIds: filters.checkboxes.userId?.length ? filters.checkboxes.userId : undefined,
    ownerIds: filters.ownerIds?.length ? filters.ownerIds : undefined,
    fromDate: filters.dateFrom ? `${filters.dateFrom}T00:00:00Z` : undefined,
    toDate: filters.dateTo ? `${filters.dateTo}T23:59:59Z` : undefined,
    orgIds: effectiveOrgIds,
    lobIds: (isSuperAdmin || isOrgAdmin) && filters.checkboxes.lobId?.length ? filters.checkboxes.lobId : undefined,
    warehouseIds: isSuperAdmin && filters.checkboxes.warehouseId?.length ? filters.checkboxes.warehouseId : undefined,
    customerTypeIds: filters.checkboxes.customerTypeId?.length ? filters.checkboxes.customerTypeId.map(Number) : undefined,
  }

  // ── Table query (regular pagination) ───────────────────────────────────────
  const apiFilters: LeadFilters = { ...baseFilters, page, pageSize: pageSizeTable }

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['leads-list', apiFilters],
    queryFn: () => leadsApi.getAll(apiFilters),
    placeholderData: prev => prev,
    staleTime: 0,
    enabled: viewMode === 'table',
  })

  const leads = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = data?.totalPages ?? 1

  const { data: summaryData } = useQuery({
    queryKey: ['leads-summary', baseFilters],
    queryFn: () => leadsApi.getSummary(baseFilters),
    staleTime: 0,
    enabled: viewMode === 'kanban',
  })

  const statusCountsMap = useMemo(() => {
    const map: Record<string, number> = {}
    if (summaryData?.statusSummaries) {
      for (const s of summaryData.statusSummaries) {
        map[s.status] = s.count
      }
    }
    return map
  }, [summaryData])

  // Complete list of sources from the DB (localStorage-cached) — NOT derived from
  // the current paginated response, so the Source filter is identical on every page.
  const { options: sourceFilterOptions } = useLeadSourceFilterOptions()

  const filterDefs: FilterDef[] = useMemo(() => {
    const defs: FilterDef[] = [
      {
        type: 'checkbox',
        key: 'status',
        label: 'Status',
        options: Array.from(new Set([...LEAD_STATUSES, ...leads.map(l => l.status)])).filter(Boolean)
          .map(s => ({ label: STATUS_LABELS[s as LeadStatus] || s, value: s })),
      },
      {
        type: 'checkbox',
        key: 'source',
        label: 'Source',
        options: sourceFilterOptions,
      },
      {
        type: 'checkbox',
        key: 'tagId',
        label: 'Tag',
        options: allTags.map(t => ({ label: t.name, value: t.id })),
      },
      {
        type: 'checkbox',
        key: 'customerTypeId',
        label: 'Customer Type',
        options: customerTypes.map(ct => ({ label: ct.name, value: String(ct.id) })),
      },
      { type: 'date', label: 'Date Created' },
    ]

    if (authStore.canViewLobWideData) {
      defs.push(
        {
          type: 'checkbox',
          key: 'userId',
          label: 'Assignee',
          options: [
            { label: 'Unassigned', value: 'unassigned' },
            ...usersList.map(u => ({ label: u.name, value: u.id })),
          ],
        },
        { type: 'owner', label: 'Owner' },
      )
    }

    if (isSuperAdmin) {
      defs.push(
        {
          type: 'checkbox',
          key: 'orgId',
          label: 'Organisation',
          options: orgs.map(o => ({ label: o.name, value: o.id })),
        },
        {
          type: 'checkbox',
          key: 'lobId',
          label: 'LOB',
          options: lobsForFilter.map(l => ({ label: l.name, value: l.id })),
        },
      )
    } else if (isOrgAdmin) {
      // Org Admin's org is fixed (no Organisation picker needed) — just let them
      // narrow the list down to one or more LOBs within their own org.
      defs.push({
        type: 'checkbox',
        key: 'lobId',
        label: 'LOB',
        options: lobsForFilter.map(l => ({ label: l.name, value: l.id })),
      })
    }
    return defs
  }, [allTags, leads, sourceFilterOptions, customerTypes, isSuperAdmin, isOrgAdmin, orgs, lobsForFilter, usersList])

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['leads-list'] })
    qc.invalidateQueries({ queryKey: ['customer-leads'] })
    qc.invalidateQueries({ queryKey: ['leads-kanban-col'] })
    qc.invalidateQueries({ queryKey: ['leads-summary'] })
  }

  const createMutation = useMutation({
    mutationFn: (data: LeadFormData) => {
      return leadsApi.create({
        name: `${data.firstName} ${data.lastName || ''}`.trim(),
        phone: data.contactNumber,
        email: data.email || null,
        source: data.source,
        companyName: data.companyName,
        address: data.address || undefined,
        gst: data.gst || undefined,
        pincode: data.pincode || undefined,
        orgId: data.orgId || undefined,
        lobId: data.lobId || undefined,
        warehouseId: data.warehouseId || undefined,
        nextAction: data.description || undefined,
        designation: data.designation || undefined,
        websiteUrl: data.companyWebsite || undefined,
        industryType: data.industry || undefined,
        customerTypeId: data.customerTypeId ?? undefined,
        fileType: data.fileType || undefined,
        requirements: data.requirements || undefined,
        bestTimeToContact: data.bestTimeToContact || undefined,
        expectedCloseDate: data.expectedCloseDate
          ? new Date(data.expectedCloseDate).toISOString()
          : undefined,
        expectedAmount: data.expectedAmount != null ? Number(data.expectedAmount) : undefined,
      })
    },
    onSuccess: (newLead) => {
      invalidate()
      setNewLeadOpen(false)
      toast.success('Lead created')
      navigate(`/leads/${newLead.id}`)
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to create lead'),
  })

  const updateMutation = useMutation({
    mutationFn: (data: UpdateLeadPayload) => leadsApi.update(editingLead!.id, data),
    onSuccess: (updatedLead) => {
      invalidate()
      qc.invalidateQueries({ queryKey: ['lead', updatedLead.id] })
      qc.invalidateQueries({ queryKey: ['lead-activities', updatedLead.id] })
      qc.invalidateQueries({ queryKey: ['lead-notes', updatedLead.id] })
      setEditingLead(null)
      toast.success('Lead updated')
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to update lead'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => leadsApi.delete(id),
    onSuccess: () => { invalidate(); setDeletingLead(null); toast.success('Lead deleted') },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to delete'),
  })

  const statusMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: LeadStatus }) => leadsApi.changeStatus(id, status),
    onSuccess: () => {
      toast.success('Status updated')
      qc.invalidateQueries({ queryKey: ['leads-list'], refetchType: 'none' })
      qc.invalidateQueries({ queryKey: ['customer-leads'], refetchType: 'none' })
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? 'Invalid transition')
      qc.invalidateQueries({ queryKey: ['leads-kanban-col'] })
      qc.invalidateQueries({ queryKey: ['leads-summary'] })
    },
  })

  const handleDropLead = (leadId: string, targetStatus: LeadStatus) => {
    let lead: Lead | undefined
    if (viewMode === 'kanban') {
      const queryCache = qc.getQueryCache().findAll({ queryKey: ['leads-kanban-col'] })
      for (const q of queryCache) {
        const d = q.state.data as any
        if (d?.pages) {
          for (const page of d.pages) {
            const found = page.items?.find((l: any) => l.id === leadId)
            if (found) { lead = found; break }
          }
        }
        if (lead) break
      }
    } else {
      lead = leads.find(l => l.id === leadId)
    }
    if (!lead) return

    if (lead.status === 'CONVERTED') {
      toast.error("Converted leads cannot be moved")
      return
    }

    const ACTIVE_STATUSES: LeadStatus[] = ['NEW', 'CONTACTED', 'QUALIFIED', 'CONVERTED']
    const currentIdx = ACTIVE_STATUSES.indexOf(lead.status)
    const targetIdx = ACTIVE_STATUSES.indexOf(targetStatus)

    if (targetStatus === 'JUNK') {
      setJunkLead(lead)
    } else if (targetIdx === currentIdx + 1) {
      if (targetStatus === 'CONVERTED') {
        setConvertLead(lead)
      } else {
        const oldStatus = lead.status
        const updatedLead = { ...lead, status: targetStatus, updatedAt: new Date().toISOString() }

        // Remove from old status column cache
        qc.setQueriesData(
          { queryKey: ['leads-kanban-col', oldStatus] },
          (old: any) => {
            if (!old?.pages) return old
            return {
              ...old,
              pages: old.pages.map((p: any) => ({
                ...p,
                items: p.items.filter((item: any) => item.id !== leadId),
                total: Math.max(0, (p.total || 0) - 1),
              })),
            }
          }
        )

        // Add to new status column cache
        qc.setQueriesData(
          { queryKey: ['leads-kanban-col', targetStatus] },
          (old: any) => {
            if (!old?.pages || old.pages.length === 0) return old
            const firstPage = old.pages[0]
            const exists = firstPage.items?.some((item: any) => item.id === leadId)
            if (exists) return old
            return {
              ...old,
              pages: [
                {
                  ...firstPage,
                  items: [updatedLead, ...firstPage.items],
                  total: (firstPage.total || 0) + 1,
                },
                ...old.pages.slice(1),
              ],
            }
          }
        )

        // Optimistically update summary counts
        qc.setQueriesData(
          { queryKey: ['leads-summary'] },
          (old: any) => {
            if (!old?.statusSummaries) return old
            return {
              ...old,
              statusSummaries: old.statusSummaries.map((s: any) => {
                if (s.status === oldStatus) return { ...s, count: Math.max(0, s.count - 1) }
                if (s.status === targetStatus) return { ...s, count: s.count + 1 }
                return s
              }),
            }
          }
        )

        statusMut.mutate({ id: lead.id, status: targetStatus })
      }
    } else if (lead.status === targetStatus) {
      // dropped in same column, do nothing
    } else {
      toast.error("Leads can only be moved step-by-step through the stages")
    }
  }

  const selectedLeads = useMemo(() => leads.filter(l => selected.has(l.id)), [leads, selected])
  const activeLeadsInSelection = useMemo(() => selectedLeads.filter(l => l.status !== 'CONVERTED' && l.status !== 'JUNK'), [selectedLeads])
  const closedLeadsInSelection = useMemo(() => selectedLeads.filter(l => l.status === 'CONVERTED' || l.status === 'JUNK'), [selectedLeads])

  const selectedOrgIds = useMemo(() => Array.from(new Set(selectedLeads.map(l => l.orgId).filter(Boolean))), [selectedLeads])
  const hasMultipleOrgsSelected = selectedOrgIds.length > 1
  const targetOrgId = selectedOrgIds.length === 1 ? selectedOrgIds[0] : null

  const selectedLobIds = useMemo(() => Array.from(new Set(selectedLeads.map(l => l.lobId).filter(Boolean))), [selectedLeads])
  const hasMultipleLobsSelected = selectedLobIds.length > 1
  const targetLobId = selectedLobIds.length === 1 ? selectedLobIds[0] : null

  // Eligibility rules (same as single-lead reassign):
  //  • Must belong to the same Organization as the selected lead(s).
  //  • Org Admins (no specific LOB — lobId is null) are eligible regardless of which LOB(s) are selected.
  //  • Normal users must belong to the exact LOB shared by all selected leads.
  //    If the selection spans multiple LOBs, no single normal user can be valid for all of them,
  //    so only Org Admins remain eligible in that case.
  //  • SUPER_ADMIN accounts are never assignable.
  const filteredUsersList = useMemo(() => {
    return usersList.filter(u => {
      if (u.roleName?.toUpperCase() === 'SUPER_ADMIN') return false
      if (targetOrgId && u.orgId !== targetOrgId) return false
      if (!u.lobId) return true // Org Admin — eligible across all LOBs in the org
      if (hasMultipleLobsSelected) return false // normal user can't cover multiple LOBs at once
      if (targetLobId && u.lobId !== targetLobId) return false
      return true
    })
  }, [usersList, targetOrgId, targetLobId, hasMultipleLobsSelected])

  const bulkAssignOptions = useMemo(() => {
    return [UNASSIGN_LEAD_USER, ...filteredUsersList]
  }, [filteredUsersList])

  const bulkDeleteMutation = useMutation({
    mutationFn: () => {
      const ids = [...selected]
      return leadsApi.bulkDelete(ids).then(() => ids.length)
    },
    onSuccess: (count) => {
      invalidate()
      setSelected(new Set())
      setBulkDeleteOpen(false)
      toast.success(`${count} lead(s) deleted`)
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to delete leads'),
  })

  const bulkAssignMutation = useMutation({
    mutationFn: () => {
      const assignableIds = activeLeadsInSelection.map(l => l.id)
      if (assignableIds.length === 0) return Promise.resolve({ count: 0, name: '', isUnassign: false })
      const isUnassign = bulkAssignUser?.id === '__UNASSIGN__'
      const name = bulkAssignUser?.name ?? ''
      return leadsApi.bulkAssign(assignableIds, isUnassign ? null : bulkAssignUser!.id).then(() => ({
        count: assignableIds.length,
        name,
        isUnassign
      }))
    },
    onSuccess: ({ count, name, isUnassign }) => {
      invalidate()
      setSelected(new Set())
      setBulkAssignOpen(false)
      setBulkAssignUser(null)
      if (isUnassign) {
        toast.success(`${count} lead(s) unassigned`)
      } else {
        toast.success(`${count} lead(s) assigned to ${name}`)
      }
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to update lead assignments'),
  })

  // ── Lead table columns ───────────────────────────────────────────────────────
  const leadColumns: Column[] = [
    {
      id: 'name', label: 'Name', sortable: true, minWidth: 200,
      render: (_v, lead) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
          <Avatar sx={{
            width: 30, height: 30, fontSize: '11px', fontWeight: 700, flexShrink: 0,
            ...getAvatarStyle(lead.name),
          }}>
            {getInitials(lead.name)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '13px', color: themeColors.primary, lineHeight: 1.3 }}>
              {lead.name}
            </Typography>
            {lead.tags.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.4, mt: 0.3, flexWrap: 'wrap' }}>
                {lead.tags.slice(0, 3).map((tag: any) => (
                  <Chip key={tag.id} label={tag.name} size="small"
                    sx={{
                      height: 14, fontSize: '9px', fontWeight: 600,
                      bgcolor: `${tag.color}18`, color: tag.color,
                      border: `1px solid ${tag.color}40`,
                      '& .MuiChip-label': { px: '4px' }
                    }} />
                ))}
                {lead.tags.length > 3 && (
                  <Typography fontSize="9px" color="text.secondary" alignSelf="center">
                    +{lead.tags.length - 3}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>
      ),
    },
    {
      id: 'company', label: 'Company', minWidth: 140,
      render: (_v, lead) => (
        <Typography fontSize="12.5px" color="text.secondary" noWrap sx={{ maxWidth: 160 }}>
          {lead.companyName || lead.orgName || '—'}
        </Typography>
      ),
    },
    {
      id: 'contactNumber', label: 'Contact Number', minWidth: 130,
      render: (_v, lead) => (
        <Typography fontSize="12.5px" color="text.secondary" noWrap sx={{ maxWidth: 160 }}>
          {maskPhoneNumber(lead.phone)}
        </Typography>
      ),
    },
    {
      id: 'source', label: 'Source', width: 110,
      render: (_v, lead) => (
        <Chip label={SOURCE_LABELS[lead.source] ?? lead.source} size="small"
          sx={{
            height: 20, fontSize: '10.5px', fontWeight: 600,
            bgcolor: '#f5f7fa', color: themeColors.text.secondary
          }} />
      ),
    },
    {
      id: 'assignedTo', label: 'Assignee', minWidth: 130,
      render: (_v, lead) => {
        return lead.assignedTo ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Avatar sx={{ width: 20, height: 20, fontSize: '9px', fontWeight: 700, ...getAvatarStyle(lead.assignedTo.name) }}>
              {getInitials(lead.assignedTo.name)}
            </Avatar>
            <Typography fontSize="12.5px" color="text.secondary" noWrap sx={{ maxWidth: 110 }}>
              {lead.assignedTo.name}
            </Typography>
          </Box>
        ) : <Typography fontSize="12.5px" color="text.disabled">—</Typography>;
      },
    },
    {
      id: 'status', label: 'Status', width: 110,
      render: (_v, lead) => <StatusBadge status={lead.status} />,
    },
    {
      id: 'createdAt', label: 'Lead Created', sortable: true, minWidth: 120,
      render: (_v, lead) => (
        <Typography fontSize="12.5px" color="text.secondary" noWrap>
          {formatDate(lead.createdAt)}
        </Typography>
      ),
    },
    {
      id: 'expectedAmount', label: 'Expected Value', sortable: true, minWidth: 120,
      render: (_v, lead) => (
        <Typography fontSize="12.5px" color="text.secondary" noWrap>
          {lead.expectedAmount != null ? `₹${lead.expectedAmount.toLocaleString('en-IN')}` : '—'}
        </Typography>
      ),
    },
    {
      id: 'actions', label: 'Action', align: 'right' as const, width: 120,
      render: (_v, lead) => {
        const isConverted = lead.status === 'CONVERTED'
        const isJunk = lead.status === 'JUNK'
        const callDisabled = isConverted || isJunk
        return (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25 }}
            onClick={e => e.stopPropagation()}>
            {canCallLog && !isJunk && (
              <Tooltip title={isConverted ? "Lead is converted" : "Call lead"}>
                <Box component="span" sx={{ display: 'inline-flex', cursor: isConverted ? 'not-allowed' : 'inherit' }}>
                  <IconButton size="small" disabled={callStore.isStarting || callDisabled}
                    onClick={() => callStore.showPlaceCall({
                      entityType: 'LEAD',
                      entityId: lead.id,
                      name: lead.name,
                      phone: lead.phone,
                      companyName: lead.companyName ?? undefined
                    })}
                    sx={{ color: '#7A7580', '&:hover': { color: '#7b1fa2', bgcolor: '#7b1fa215' } }}>
                    {callStore.isStarting && callStore.target?.entityId === lead.id
                      ? <CircularProgress size={13} sx={{ color: '#7b1fa2' }} />
                      : <CallIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                </Box>
              </Tooltip>
            )}

            {canEdit && !isJunk && (
              <Tooltip title={isConverted ? "Lead is converted" : "Edit lead"}>
                <Box component="span" sx={{ display: 'inline-flex', cursor: isConverted ? 'not-allowed' : 'inherit' }}>
                  <IconButton size="small" onClick={() => setEditingLead(lead)}
                    disabled={isConverted}
                    sx={{ color: '#7A7580', '&:hover': { color: '#2F1E57', bgcolor: '#F2EEF9' } }}>
                    <EditActionIcon />
                  </IconButton>
                </Box>
              </Tooltip>
            )}

            {canDelete && (
              <Tooltip title="Delete lead">
                <IconButton size="small" onClick={() => setDeletingLead(lead)}
                  sx={{ color: '#D92D20', '&:hover': { bgcolor: '#FFE5E5' } }}>
                  <DeleteActionIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )
      },
    },
  ]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        title="Leads"
        breadcrumb="CRM"
        actions={
          <PageActionButtons
            onBulkUpload={canBulkUpload ? () => bulkUploadStore.setOpen(true) : undefined}
            onNew={canCreate ? () => setNewLeadOpen(true) : undefined}
            onExport={canExport ? async () => {
              await leadsApi.export({ ...apiFilters, search })
            } : undefined}
            newLabel="New Lead"
          />
        }
      />


      {/* ── Pinned toolbar (search + filters) ── */}
      <Box sx={{ px: 3, pt: 3, pb: 0, flexShrink: 0 }}>
        <ListToolbar
          search={search}
          onSearch={(v) => { setSearch(v); setPage(1) }}
          searchPlaceholder="Search name, phone, email..."
          filterDefs={filterDefs}
          filters={filters}
          onFilterChange={(f) => { setFilters(f); setPage(1) }}
          onRefresh={() => refetch()}
          isRefreshing={isFetching}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          selectedCount={viewMode === 'table' ? selected.size : 0}
          onClearSelection={() => setSelected(new Set())}
          bulkActions={useMemo(() => {
            const actions: BulkAction[] = []
            if (canAssign) {
              actions.push({
                label: 'Assign to…',
                onClick: () => setBulkAssignOpen(true),
              })
            }
            if (canDelete) {
              actions.push({
                label: 'Delete',
                color: 'error',
                onClick: () => setBulkDeleteOpen(true),
              })
            }
            return actions
          }, [canAssign, canDelete])}
        />
      </Box>

      {/* ── Scrollable content ── */}
      <Box
        ref={scrollContainerRef}
        onScroll={handleScroll}
        sx={{ flex: 1, px: 3, pb: 3, pt: 2, overflowY: 'auto', position: 'relative' }}
      >

        {/* ── Kanban view ── */}
        {viewMode === 'kanban' && (
          <>
            <KanbanBoard
              statusCounts={statusCountsMap}
              baseFilters={baseFilters}
              onOpen={lead => navigate(`/leads/${lead.id}`)}
              onCall={lead => callStore.showPlaceCall({
                entityType: 'LEAD',
                entityId: lead.id,
                name: lead.name,
                phone: lead.phone,
                companyName: lead.companyName ?? undefined
              })}
              onFollowUp={lead => setFollowUpLead(lead)}
              onAddLead={() => setNewLeadOpen(true)}
              canCreate={canCreate}
              onDropLead={handleDropLead}
            />

            {/* Scroll to top button */}
            {showScrollTop && (
              <Tooltip title="Scroll to top" placement="left">
                <IconButton
                  onClick={() => scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
                  sx={{
                    position: 'fixed',
                    bottom: 28,
                    right: 28,
                    zIndex: 1200,
                    bgcolor: themeColors.primary,
                    color: '#ffffff',
                    width: 44,
                    height: 44,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.22)',
                    '&:hover': {
                      bgcolor: themeColors.primary,
                      filter: 'brightness(1.1)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
                    },
                    transition: 'all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  }}
                >
                  <ScrollTopIcon sx={{ fontSize: 24 }} />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}

        {/* ── Table view ── */}
        {viewMode === 'table' && (
          <DataTable
            columns={leadColumns}
            rows={leads}
            isLoading={isLoading}
            showRowNumbers={false}
            onRowClick={lead => navigate(`/leads/${lead.id}`)}
            emptyMessage={search || countActiveFilters(filters) > 0 ? 'No leads match your filters' : 'No leads yet'}
            emptyHint={search || countActiveFilters(filters) > 0 ? 'Try adjusting your filters' : 'Create your first lead to get started'}
            emptyIcon={<LeadsIcon />}
            selectedIds={selected}
            onSelectionChange={setSelected}
            pagination={{
              page,
              pageSize: pageSizeTable,
              total,
              totalPages,
              itemLabel: 'leads',
              onPageChange: p => setPage(p),
              onPageSizeChange: s => { setPageSizeTable(s); localStorage.setItem('crm_leads_page_size', String(s)); setPage(1) },
              pageSizeOptions: [10, 20, 50, 100],
            }}
          />
        )}
      </Box>

      {/* ── Dialogs ── */}
      {canCreate && (
        <LeadDrawer
          open={newLeadOpen}
          onClose={() => setNewLeadOpen(false)}
          isSubmitting={createMutation.isPending}
          onSubmit={async (data) => {
            await createMutation.mutateAsync(data)
          }}
        />
      )}

      {editingLead && (
        <EditLeadDialog
          open={!!editingLead}
          lead={editingLead}
          onClose={() => setEditingLead(null)}
          onSave={async (payload) => {
            await updateMutation.mutateAsync(payload)
          }}
          isSaving={updateMutation.isPending}
        />
      )}

      <DeleteDialog
        open={!!deletingLead}
        title="Delete Lead"
        name={deletingLead?.name}
        description={deletingLead ? `Are you sure you want to delete ${deletingLead.name}? This will permanently remove the lead and all its activity history.` : undefined}
        onClose={() => setDeletingLead(null)}
        onConfirm={() => deletingLead && deleteMutation.mutate(deletingLead.id)}
        isDeleting={deleteMutation.isPending}
      />

      {followUpLead && (
        <FollowUpModal
          leadId={followUpLead.id}
          leadName={followUpLead.name}
          open={!!followUpLead}
          onClose={() => setFollowUpLead(null)}
          onSaved={() => { setFollowUpLead(null); invalidate() }}
        />
      )}

      {/* ── Bulk delete confirmation ── */}
      <DeleteDialog
        open={bulkDeleteOpen}
        title="Delete Leads"
        name={`${selected.size} selected lead${selected.size > 1 ? 's' : ''}`}
        description={`Are you sure you want to permanently delete ${selected.size} lead${selected.size > 1 ? 's' : ''}? This cannot be undone.`}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={() => bulkDeleteMutation.mutate()}
        isDeleting={bulkDeleteMutation.isPending}
      />

      {/* ── Bulk assign dialog ── */}
      <Dialog open={bulkAssignOpen} onClose={() => { setBulkAssignOpen(false); setBulkAssignUser(null) }}
        PaperProps={{ sx: { borderRadius: '12px', minWidth: 380 } }}>
        <DialogTitle sx={{ fontSize: '15px', fontWeight: 700, pb: 1 }}>
          {bulkAssignUser?.id === '__UNASSIGN__' ? 'Unassign' : 'Assign'} {activeLeadsInSelection.length} lead{activeLeadsInSelection.length !== 1 ? 's' : ''}
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          {closedLeadsInSelection.length > 0 && (
            <Typography fontSize="12.5px" color="warning.main" sx={{ pb: 1, fontWeight: 600 }}>
              ⚠️ {closedLeadsInSelection.length} selected lead(s) are Converted/Junk and cannot be modified. Only active leads will be updated.
            </Typography>
          )}
          {hasMultipleOrgsSelected ? (
            <Typography fontSize="13px" color="error" sx={{ py: 1, fontWeight: 500 }}>
              ⚠️ The selected leads belong to different organizations. Bulk assignment is only allowed for leads belonging to the same organization.
            </Typography>
          ) : (
            <>
              {hasMultipleLobsSelected && (
                <Typography fontSize="12px" color="text.secondary" sx={{ pb: 1 }}>
                  Selected leads span multiple LOBs — only Organization Admins are eligible for bulk assignment (or choose Unassign).
                </Typography>
              )}
              <Autocomplete
                options={bulkAssignOptions}
                getOptionLabel={(u) => u.name}
                value={bulkAssignUser}
                onChange={(_e, v) => setBulkAssignUser(v)}
                noOptionsText="No eligible users in this LOB / org"
                renderInput={(params) => (
                  <FormInput {...params} label="Select assignee or unassign" size="small" autoFocus />
                )}
                renderOption={(props, u) => {
                  const isUnassign = u.id === '__UNASSIGN__'
                  return (
                    <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: '6px !important' }}>
                      <Avatar sx={{
                        width: 24, height: 24, fontSize: '11px', fontWeight: 700,
                        ...(isUnassign
                          ? { bgcolor: '#fef2f2', color: '#ef4444', border: '1px solid #fca5a5' }
                          : getAvatarStyle(u.name))
                      }}>
                        {isUnassign ? '✕' : u.name.split(' ').slice(0, 2).map((n: string) => n[0]?.toUpperCase() ?? '').join('')}
                      </Avatar>
                      <Box>
                        <Typography fontSize="13px" fontWeight={600} color={isUnassign ? 'error.main' : 'inherit'}>
                          {u.name}
                        </Typography>
                        <Typography fontSize="11px" color="text.secondary">
                          {u.email}
                        </Typography>
                      </Box>
                    </Box>
                  )
                }}
                isOptionEqualToValue={(a, b) => a.id === b.id}
              />
              {filteredUsersList.length === 0 && (
                <Typography fontSize="12px" color="error" sx={{ pt: 1 }}>
                  No eligible users found to assign. You can still select Unassign to clear assignees.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => { setBulkAssignOpen(false); setBulkAssignUser(null) }}
            size="small" sx={{ textTransform: 'none', color: '#6B7280' }}>
            Cancel
          </Button>
          <Button
            variant="contained" size="small" disabled={hasMultipleOrgsSelected || activeLeadsInSelection.length === 0 || !bulkAssignUser || bulkAssignMutation.isPending}
            onClick={() => bulkAssignMutation.mutate()}
            sx={{
              textTransform: 'none',
              bgcolor: bulkAssignUser?.id === '__UNASSIGN__' ? '#ef4444' : '#7C3AED',
              '&:hover': { bgcolor: bulkAssignUser?.id === '__UNASSIGN__' ? '#dc2626' : '#6D28D9' }
            }}>
            {bulkAssignMutation.isPending ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : (bulkAssignUser?.id === '__UNASSIGN__' ? 'Unassign' : 'Assign')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!junkLead}
        title="Mark Lead as Junk"
        description={junkLead ? `Are you sure you want to mark ${junkLead.name} as Junk? This will update the lead's status to JUNK.` : ''}
        confirmLabel="Mark as Junk"
        confirmColor="error"
        isConfirming={statusMut.isPending}
        onClose={() => setJunkLead(null)}
        onConfirm={() => {
          if (junkLead) {
            statusMut.mutate({ id: junkLead.id, status: 'JUNK' }, {
              onSuccess: () => {
                setJunkLead(null)
              }
            })
          }
        }}
      />

      <ConvertLeadFlow
        open={!!convertLead}
        lead={convertLead}
        onClose={() => setConvertLead(null)}
        onSuccess={invalidate}
      />


      {/* Global calling UI handle (rendered in AppLayout) */}
    </Box>
  )
})

export default LeadsPage